import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { useI18N, useResponsive } from "@sundae/react-hooks";
import { TabsV3, Text, Tooltip, toolkitCx } from "@sundae/ui-toolkit";
import { AssetAmount } from "@sundaeswap/asset";
import { SundaeUtils } from "@sundaeswap/core/utilities";
import cx from "classnames";
import isEmpty from "lodash/isEmpty";
import { FC, useCallback, useState } from "react";
import { joyrideSelectors } from "../../../constants/joyride.constants";
import { useTasteTestContext } from "../../../pages/TasteTestDetail/context";
import { IAssetMetaData } from "../../../types/Asset.types";
import BalanceWithAssetName from "../../BalanceWithAssetName";
import FadeInBasic from "../../FadeInView/FadeInBasic";
import TasteTestEmptyChartView from "../TasteTestEmptyChartView";
import TasteTestTokenomicsDoughnutChart from "../TasteTestTokenomicsDoughnutChart";
import TasteTestVestingSchedule from "../TasteTestVestingSchedule";
const {
  theme: { colors },
} = require("@sundae/tailwind-config");
const defaultPrimaryColor = colors.primary["DEFAULT"];

const TasteTestTokenomics: FC = () => {
  const { currentLocale, t } = useI18N("taste-test");
  const { sm, lg: largeScreen } = useResponsive();
  const {
    state: { tasteTest, tasteTestPersonalization },
  } = useTasteTestContext();
  const isVestingInformationAvailable = !isEmpty(tasteTest?.vestingSchedule);
  const [tabValue, setTabValue] = useState<"vesting" | "tokenomics">(
    isVestingInformationAvailable ? "vesting" : "tokenomics"
  );

  const handleTabChange = useCallback((value: string) => {
    setTabValue(value as "vesting" | "tokenomics");
  }, []);

  return (
    <div
      data-guide-selector={joyrideSelectors.guides.tasteTest.interactiveGuide.tokenomics}
      style={{ color: tasteTestPersonalization?.primaryColor || defaultPrimaryColor }}
    >
      <TabsV3
        withBorder={false}
        value={tabValue}
        size="lg"
        primitiveProps={{
          trigger: { className: "!pl-0", value: tabValue },
        }}
        onValueChange={handleTabChange}
        data={[
          ...(isVestingInformationAvailable
            ? [
                {
                  id: "vesting",
                  content: <TasteTestVestingSchedule />,
                  trigger: (
                    <div
                      className="flex items-center gap-2"
                      style={{
                        color: tasteTestPersonalization?.primaryColor || defaultPrimaryColor,
                      }}
                    >
                      <Text
                        className="flex items-center gap-2"
                        tag="span"
                        size="lg"
                        variant={
                          tabValue !== "vesting"
                            ? "normal"
                            : tasteTestPersonalization?.primaryColor
                            ? "current"
                            : "info"
                        }
                      >
                        {t("detail.projectDetails.vestingSchedule.title", {
                          context: !sm ? "short" : undefined,
                        })}
                        {largeScreen && (
                          <Tooltip
                            tooltipLabel={t("detail.projectDetails.vestingSchedule.warning")}
                            trigger={
                              <div className="cursor-help">
                                <ExclamationTriangleIcon
                                  className={cx("h-6 w-6", toolkitCx.layout.text.warning)}
                                />
                              </div>
                            }
                          />
                        )}
                      </Text>
                    </div>
                  ),
                },
              ]
            : []),
          {
            id: "tokenomics",
            content: !tasteTest?.projectDetails.tokenomics.length ? (
              /* -------------------------------------------------------------------------------------------------
               * Notice for no available Tokenomics Information
               * -----------------------------------------------------------------------------------------------*/
              <TasteTestEmptyChartView
                title={t("detail.projectDetails.tokenomics.noData", { context: "title" })}
                description={t("detail.projectDetails.tokenomics.noData")}
              />
            ) : (
              /*
                -------------------------------------------------------------------------------------------------
                * Happy path for Tokenomics Information *
                -----------------------------------------------------------------------------------------------*/
              <FadeInBasic className="relative flex flex-col gap-4 rounded-lg pt-4 lg:pt-0 xl:max-h-[28rem]">
                <>
                  <div className="flex items-center gap-2">
                    <Text tag="span" size="sm" variant="muted">
                      {t("detail.projectDetails.tokenomics.totalSupply")}:
                    </Text>
                    <BalanceWithAssetName
                      abbreviate={false}
                      asset={
                        (tasteTest?.asset as unknown as IAssetMetaData) ?? ({} as IAssetMetaData)
                      }
                      locale={currentLocale}
                      privacy={false}
                      size="sm"
                      showSymbol={tasteTest && SundaeUtils.isAdaAsset(tasteTest.asset)}
                      value={
                        new AssetAmount<IAssetMetaData>(
                          tasteTest?.asset.totalSupply,
                          (tasteTest?.asset as unknown as IAssetMetaData) ?? ({} as IAssetMetaData)
                        )
                      }
                    />
                  </div>

                  <div className="relative mx-auto my-auto flex h-fit max-h-96 w-full items-center justify-center lg:col-span-1 lg:h-[486px]">
                    <TasteTestTokenomicsDoughnutChart />
                  </div>
                </>
              </FadeInBasic>
            ),
            trigger: (
              <div
                className="flex items-center gap-2"
                style={{
                  color: tasteTestPersonalization?.primaryColor || defaultPrimaryColor,
                }}
              >
                <Text
                  tag="div"
                  size="lg"
                  className="flex items-center gap-2"
                  variant={
                    tabValue !== "tokenomics"
                      ? "normal"
                      : tasteTestPersonalization?.primaryColor
                      ? "current"
                      : "info"
                  }
                >
                  {t("detail.projectDetails.tokenomics.title", {
                    context: !sm ? "short" : undefined,
                  })}
                  {largeScreen && (
                    <Tooltip
                      tooltipLabel={t("detail.projectDetails.tokenomics.warning")}
                      trigger={
                        <div className="cursor-help">
                          <ExclamationTriangleIcon
                            className={cx("h-6 w-6", toolkitCx.layout.text.warning)}
                          />
                        </div>
                      }
                    />
                  )}
                </Text>
              </div>
            ),
          },
        ]}
      />
    </div>
  );
};

export default TasteTestTokenomics;
